import { SparklesIcon } from "@heroicons/react/24/outline";

import Modal from "../../../layouts/Modal";
import { ISerpApiSearchResult } from "../../../models/PartLookup";
import PriceFormat from "../../../utils/priceFormat";

export default function UnlistedIntelligenceModal({
  open,
  setOpen,
  active,
  sold,
  loading,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  active: ISerpApiSearchResult[] | null;
  sold: ISerpApiSearchResult[] | null;
  loading: boolean;
}) {

  const activeAvg = active && active.length > 0 ? PriceFormat(active.reduce((sum, r) => sum + (r.priceExtracted ?? 0), 0) / active.length) : null;
  const soldAvg = sold && sold.length > 0 ? PriceFormat(sold.reduce((sum, r) => sum + (r.priceExtracted ?? 0), 0) / sold.length) : null;

  return (
    <Modal open={open} setOpen={setOpen} width={"max-w-5xl"}>
      <div className="flex items-center gap-2 border-b pb-3">
        <SparklesIcon className="h-5 w-5 text-amber-500" aria-hidden="true" />
        <h3 className="text-lg font-semibold text-gray-900">Wilma Intelligence — eBay matches</h3>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="gap-y-2">
          <p className="text-sm text-gray-500 px-2">
            There are {active?.length ?? 0} active listings{activeAvg ? ` with an average of ${activeAvg}` : ""}.
          </p>
          <IntelligenceColumn title="Active" loading={loading} results={active} />
        </div>

        <div>
          <p className="text-sm text-gray-500 px-2">
            There are {sold?.length ?? 0} recently sold listings{soldAvg ? ` with an average of ${soldAvg}` : ""}.
          </p>

          <IntelligenceColumn title="Sold" loading={loading} results={sold} />
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
          onClick={() => setOpen(false)}
        >
          Close
        </button>
      </div>
    </Modal>
  );
}

function IntelligenceColumn({
  title,
  loading,
  results,
}: {
  title: string;
  loading: boolean;
  results: ISerpApiSearchResult[] | null;
}) {
  return (
    <div className="flex max-h-[60vh] flex-col rounded-lg border border-gray-200">
      <div className="flex items-center justify-between border-b bg-gray-50 px-3 py-2">
        <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
        <span className="text-xs text-gray-500">
          {results == null ? "" : `${results.length} listing${results.length === 1 ? "" : "s"}`}
        </span>
      </div>
      <div className="overflow-y-auto">
        {loading ? (
          <div className="p-6 text-center text-sm text-gray-500">Loading…</div>
        ) : results == null || results.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">No results</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {results.map((r, idx) => (
              <li key={`${r.link}-${idx}`} className="p-2">
                <a
                  href={r.link}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-start gap-3 rounded-md p-1 hover:bg-gray-50"
                >
                  {r.thumbnail ? (
                    <img
                      src={r.thumbnail}
                      alt=""
                      className="h-14 w-14 flex-shrink-0 rounded object-cover ring-1 ring-gray-200"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-14 w-14 flex-shrink-0 rounded bg-gray-100 ring-1 ring-gray-200" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 text-xs text-gray-800">{r.title}</p>
                    <p className="line-clamp-2 text-xs text-gray-800">
                      {r.soldDate ? `Sold: ${new Date(r.soldDate).toLocaleDateString()}` : ""}
                    </p>
                    <p className="mt-1 text-sm font-semibold tabular-nums text-gray-900">
                      {r.priceExtracted != null ? PriceFormat(r.priceExtracted) : r.priceRaw ?? "-"}
                    </p>
                    
                  </div>
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
